<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import Chart from 'primevue/chart';
import type { ChartOptions } from 'chart.js';
import { useEndpoint, useStringFunctions, useUtilities } from '@apparatix/composables';
import type {
  OpportunityDetail,
  RevenueProjection,
  RevenueProjectionResponse,
  ProjectionsDataset,
} from '@apparatix/types/Leads';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import TimesCircleIcon from '@primeicons/vue/times-circle';
import CheckCircleIcon from '@primeicons/vue/check-circle';

const props = defineProps<{ opportunity: OpportunityDetail, graphReload: number }>();
const emit = defineEmits<{ (e: 'on:chart-loaded'): void }>();

const projections = ref<RevenueProjection[]>();
const style = ref<CSSStyleDeclaration>();
const threshold = ref(0);

const areAllValuesZero = (values: number[]) => values.every(value => value === 0);

const {
  isLoading, makeRequest, didError,
} = useEndpoint<RevenueProjectionResponse>({
  url: `/crm/opportunities/projections/${props.opportunity.opportunity.id}`,
  type: 'GET',
  successField: 'data',
  startInLoadingState: true,
  toast: { showOnSuccess: false },
  onSuccess: response => {
    const { projectedData, thresholdCi } = response.data;
    projections.value = projectedData;
    threshold.value = thresholdCi;
    const allValues = [
      ...projectedData.map(p => p.sold),
      ...projectedData.map(p => p.projected),
      // ...projectedData.map(p => p.overage),
      ...projectedData.map(p => p.deficit),
      ...projectedData.map(p => p.budget),
    ];
    if (areAllValuesZero(allValues)) {
      emit('on:chart-loaded');
    }
  },
});

const { formatAsCurrency, isEmptyString } = useStringFunctions();
const { isNumberEmpty } = useUtilities();


const options: ChartOptions<'bar'> = {
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      ticks: { callback: (value: number) => formatAsCurrency(value, false) },
    },
  },
  interaction: {
    mode: 'index',
    intersect: true,
  },
  plugins: {
    tooltip: {
      mode: 'index',
      intersect: false,
      usePointStyle: true,
      boxWidth: 16,
      boxHeight: 16,
      callbacks: {
        labelPointStyle: ctx => ({
          pointStyle: (ctx.dataset as any).pointStyle,
          rotation: 0,
        }),
      },
    },
    legend: {
      labels: {
        usePointStyle: true,
        pointStyleWidth: 40,
      },
    },
  },
};

const dataset = computed(() => {
  if (!projections.value || !style.value) return null;

  const sold = projections.value.map(projection => projection.sold);
  const projected = projections.value.map(projection => projection.projected);
  // const overage = projections.value.map(projection => projection.overage);
  const deficit = projections.value.map(projection => projection.deficit);
  const budget = projections.value.map(projection => projection.budget);

  const data: { labels: string[], datasets: ProjectionsDataset[] } = {
    labels: projections.value.map(projection => projection.yearMonth),
    datasets: [
      {
        type: 'bar',
        label: 'Sold',
        backgroundColor: style.value.getPropertyValue('--primary-green'),
        data: sold,
        pointStyle: 'rect',
      },
      {
        type: 'bar',
        label: 'Projected',
        backgroundColor: style.value.getPropertyValue('--primary-blue-300'),
        data: projected,
        pointStyle: 'rect',
      },
      {
        type: 'bar',
        label: 'Deficit',
        backgroundColor: style.value.getPropertyValue('--danger-background'),
        data: deficit,
        pointStyle: 'rect',
      },
    ],
  };

  if (!areAllValuesZero(budget)) {
    data.datasets.unshift({
      type: 'line',
      label: 'AE Budget',
      borderColor: style.value.getPropertyValue('--primary-text-blue'),
      borderWidth: 2,
      fill: false,
      tension: 0.5,
      data: budget,
      pointStyle: 'line',
      pointRadius: 0,
      pointHoverRadius: 5,
    });
  }

  return data;
});

const shouldShowGraph = computed(() => {
  if (needsAny.value || !projections.value || projections.value?.length === 0 || isLoading.value || didError.value) return false;

  return !projections.value.every(projection =>
    projection.budget === 0 &&
    projection.sold === 0 &&
    projection.projected === 0 &&
    projection.total === 0 &&
    // projection.overage === 0 &&
    projection.deficit === 0);
});

const needsIntervalRate = computed(() => isNumberEmpty(props.opportunity?.revenueData?.rate, true, true));
const needsPeriod = computed(() => isNumberEmpty(props.opportunity?.revenueData?.periods, true, true));
const needsAnticipatedStart = computed(() =>
  !props.opportunity?.opportunity?.anticipatedStart
  || (typeof props.opportunity?.opportunity?.anticipatedStart === 'string' && props.opportunity?.opportunity?.anticipatedStart === '0000-00-00'),
);
const needsCi = computed(() => isNumberEmpty(props.opportunity?.revenueData?.ciValue));// changed from .ci @todo, do we want zeroIsEmpty??
const needsInterval = computed(() => isEmptyString(props.opportunity?.revenueData?.interval));
const needsAboveMinCi = computed(() => props.opportunity?.revenueData?.ciValue < threshold.value);

const needsAny = computed(() => needsIntervalRate.value || needsPeriod.value || needsAnticipatedStart.value || needsCi.value || needsInterval.value || needsAboveMinCi.value);

watch(
  () => props.graphReload,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {// Stripped out check because it was against stale data. @todo: need better check to skip flip brief render
      setTimeout(() => {
        makeRequest();
        style.value = getComputedStyle(document.documentElement);
      }, 1200);// Timeout is set because reload of graph seems to be faster than the data can update
      // @todo: probably a better way to handle this, but it gets us to MVP
    }
  },
);

onMounted(() => {
  makeRequest();
  style.value = getComputedStyle(document.documentElement);
});
</script>
<template>
  <div class="w-full">
    <FormSkeleton v-if="isLoading" />
    <div v-if="shouldShowGraph" class="w-full" style="position: relative;">
      <Chart
        type="bar"
        :data="dataset"
        :options="options"
        @loaded="emit('on:chart-loaded')"
      />
    </div>
    <div v-if="!isLoading && !shouldShowGraph" class="flex flex-col gap-4">
      <p class="text-lg font-normal text-[var(--swimlane-primary-text)]">
        No projections for this opportunity
      </p>
      <p class="text-base font-normal text-[var(--swimlane-primary-text)]">
        In order to see projections for this opportunity, you&rsquo;ll need to enter the following information
      </p>
      <ul class="list-inside list-none">
        <li class="flex items-center gap-2">
          <TimesCircleIcon v-if="needsIntervalRate" color="var(--tw-text-red-500)" />
          <CheckCircleIcon v-else color="var(--tw-text-green-500)" />
          Interval Rate
        </li>
        <li class="flex items-center gap-2">
          <TimesCircleIcon v-if="needsPeriod" color="var(--tw-text-red-500)" />
          <CheckCircleIcon v-else color="var(--tw-text-green-500)" />
          Periods
        </li>
        <li class="flex items-center gap-2">
          <TimesCircleIcon v-if="needsAnticipatedStart" color="var(--tw-text-red-500)" />
          <CheckCircleIcon v-else color="var(--tw-text-green-500)" />
          Anticipated Start
        </li>
        <li class="flex items-center gap-2">
          <TimesCircleIcon v-if="needsCi" color="var(--tw-text-red-500)" />
          <CheckCircleIcon v-else color="var(--tw-text-green-500)" />
          Confidence
        </li>
        <li class="flex items-center gap-2">
          <TimesCircleIcon v-if="needsAboveMinCi" color="var(--tw-text-red-500)" />
          <CheckCircleIcon v-else color="var(--tw-text-green-500)" />
          Met Confidence Threshold
        </li>
        <li class="flex items-center gap-2">
          <TimesCircleIcon v-if="needsInterval" color="var(--tw-text-red-500)" />
          <CheckCircleIcon v-else color="var(--tw-text-green-500)" />
          Interval
        </li>
      </ul>
    </div>
  </div>
</template>
